import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

function Print() {
  const { schoolId } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`/api/admin/${schoolId}/orders`);
      setOrders(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load orders');
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Helper function to check if an option is selected and return appropriate styling
  const getItemStyle = (order, category, option) => {
    let isSelected = false;

    switch (category) {
      case 'bread':
        isSelected = order.bread === option;
        break;
      case 'cheese':
        isSelected = order.cheese === option;
        break;
      case 'meat':
        isSelected = order.meat === option;
        break;
      case 'condiments':
        isSelected = order.condiments && order.condiments.includes(option);
        break;
      case 'toppings':
        isSelected = order.toppings && order.toppings.includes(option);
        break;
      case 'cookie':
        isSelected = order.cookie === option;
        break;
      case 'side':
        isSelected = order.side === option;
        break;
      default:
        isSelected = false;
    }

    return {
      padding: '4px 6px',
      border: '1px solid #ddd',
      textAlign: 'center',
      backgroundColor: 'white',
      color: isSelected ? 'black' : '#666',
      fontWeight: isSelected ? 'bold' : 'normal',
      fontSize: '0.7rem',
      lineHeight: '1.2'
    };
  };

  // Get orders that are ready to be printed (ordered status AND paid), sorted by grade and name
  const ordersToPrint = orders
    .filter(order => order.status === 'ordered' && order.paid === true)
    .sort((a, b) => {
      // First sort by grade
      const gradeA = a.grade || '';
      const gradeB = b.grade || '';
      if (gradeA !== gradeB) {
        return gradeA.localeCompare(gradeB);
      }
      // Then sort by name
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });

  const printOrders = async () => {
    try {
      if (ordersToPrint.length === 0) {
        alert('No orders to print. Only paid orders with "ordered" status will be printed.');
        return;
      }

      // Use the browser's print functionality
      window.print();

      // Ask user to mark as printed after printing
      setTimeout(async () => {
        const confirmMarkPrinted = window.confirm(
          `Have you finished printing the ${ordersToPrint.length} orders?\n\nClick "OK" if you successfully printed the orders and want to mark them as printed.\nClick "Cancel" if you had issues printing.`
        );

        if (confirmMarkPrinted) {
          try {
            console.log('Marking orders as printed:', ordersToPrint.map(o => o.id));
            await Promise.all(
              ordersToPrint.map(order =>
                axios.put(`/api/admin/${schoolId}/orders/${order.id}/status`, { status: 'printed' })
              )
            );
            alert(`Successfully marked ${ordersToPrint.length} orders as printed.`);
            // Refresh orders after marking as printed
            fetchOrders();
          } catch (err) {
            console.error('Error marking orders as printed:', err);
            alert('Failed to mark some orders as printed. Please check and update manually.');
          }
        }
      }, 2000);
    } catch (error) {
      console.error('Error printing orders:', error);
      alert('Failed to print orders. Please try again.');
    }
  };

  if (loading) return <div>Loading orders...</div>;
  if (error) return <div>Error: {error}</div>;

  const currentDate = new Date().toLocaleDateString();
  const currentTime = new Date().toLocaleTimeString();

  return (
    <div>
      {/* Print Controls - Hidden when printing */}
      <div className="no-print" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <div>
          <h3 style={{margin: '0 0 0.5rem 0'}}>Print Preview</h3>
          <p style={{margin: 0, fontSize: '0.9rem', color: '#666'}}>
            {ordersToPrint.length} orders ready to print
          </p>
        </div>
        <button
          onClick={printOrders}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          🖨️ Print Orders
        </button>
      </div>

      {/* Print Content */}
      <div className="print-content" style={{
        fontFamily: 'Arial, sans-serif',
        margin: '20px',
        backgroundColor: 'white',
        minHeight: '100vh'
      }}>
        {/* Header */}
        <div className="no-print" style={{
          textAlign: 'center',
          marginBottom: '30px',
          borderBottom: '3px solid #333',
          paddingBottom: '15px'
        }}>
        </div>

        {/* Orders */}
        {ordersToPrint.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '50px',
            color: '#999',
            fontSize: '1.2rem'
          }}>
            No orders ready to print
          </div>
        ) : (
          // Group orders by grade and create pages
          (() => {
            // Group orders by grade
            const ordersByGrade = {};
            ordersToPrint.forEach(order => {
              const grade = order.grade || 'Unknown';
              if (!ordersByGrade[grade]) {
                ordersByGrade[grade] = [];
              }
              ordersByGrade[grade].push(order);
            });

            const grades = Object.keys(ordersByGrade).sort();

            return grades.map((grade, gradeIndex) => (
              <div key={`grade-${grade}`} style={{
                pageBreakBefore: gradeIndex > 0 ? 'always' : 'auto',
                pageBreakAfter: 'auto'
              }}>
                {/* Orders for this grade, arranged in reading order */}
                {(() => {
                  const gradeOrders = ordersByGrade[grade];

                  // Calculate how many pages we need (3 orders per page)
                  const totalPages = Math.ceil(gradeOrders.length / 3);
                  const pages = [];

                  // Arrange orders in reading order:
                  // Fill left-to-right: orders 0,1,2 on page 1; 3,4,5 on page 2, etc.
                  for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
                    const pageOrders = [];

                    // Get up to 3 orders for this page in sequential order
                    for (let i = 0; i < 3; i++) {
                      const orderIndex = pageIndex * 3 + i;
                      if (orderIndex < gradeOrders.length) {
                        pageOrders.push(gradeOrders[orderIndex]);
                      }
                    }

                    pages.push(pageOrders);
                  }

                  return pages.map((pageOrders, pageIndex) => (
                    <div key={`${grade}-page-${pageIndex}`} style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      marginBottom: '30px',
                      pageBreakInside: 'avoid',
                      gap: '10px'
                    }}>
                      {pageOrders.map(order => (
                  <div key={order.id} style={{
                    border: '2px none #ddd',
                    padding: '15px',
                    //borderRadius: '8px',
                    backgroundColor: 'white',
                    width: '290px',
                    flexShrink: 0,
                    fontSize: '0.85rem'
                  }}>
                    {/* Order Header */}
                    <div style={{
                      marginBottom: '10px',
                      borderBottom: '1px solid #333',
                      paddingBottom: '8px'
                    }}>
                      <div style={{margin: 0, fontSize: '1rem', fontWeight: 'bold'}}>
                        Name: {order.name}
                      </div>
                      <div style={{margin: 0, fontSize: '1rem', fontWeight: 'bold'}}>
                        Grade: {order.grade}{order.teacher && `  •  Teacher: ${order.teacher}`}
                      </div>
                      <div style={{margin: 0, fontSize: '1rem', fontWeight: 'bold'}}>
                        {order.order_date ? new Date(order.order_date.delivery_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown'}
                      </div>
                    </div>

                    {/* Order Details Table */}
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      backgroundColor: 'white',
                      border: '1px solid #333',
                      fontSize: '0.75rem',
                      tableLayout: 'fixed'
                    }}>
                    {/* Define column widths - 6 equal columns at 16.67% each */}
                    <colgroup>
                      <col style={{width: '16.67%'}} />
                      <col style={{width: '16.67%'}} />
                      <col style={{width: '16.67%'}} />
                      <col style={{width: '16.67%'}} />
                      <col style={{width: '16.67%'}} />
                      <col style={{width: '16.67%'}} />
                    </colgroup>
                    {/* Top Category Header */}
                    <tr>
                      <td colSpan="6" className="category-header" style={{
                        backgroundColor: '#000',
                        color: 'white',
                        padding: '4px 8px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: '0.8rem'
                      }}>
                      </td>
                    </tr>

                    <tr>
                      <td colSpan="6" style={{
                        padding: '4px 8px',
                        textAlign: 'center',
                        fontSize: '0.8rem'
                      }}>6" Meal
                      </td>
                    </tr>

                    {/* Sandwich Category Header */}
                    <tr>
                      <td colSpan="6" className="category-header" style={{
                        backgroundColor: '#000',
                        color: 'white',
                        padding: '4px 8px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: '0.8rem'
                      }}>
                      </td>
                    </tr>

                {/* Sandwich Items */}
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'bread', 'White')}>
                    {order.bread === 'White' ? '✓ White ✓' : 'White'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'bread', 'Wheat')}>
                    {order.bread === 'Wheat' ? '✓ Wheat ✓' : 'Wheat'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'bread', 'Parmesan Herb')}>
                    {order.bread === 'Parmesan Herb' ? '✓ Parmesan Herb ✓' : 'Parmesan Herb'}
                  </td>
                  <td colspan="3" style={{
                    padding: '4px 6px',
                    border: '1px solid #ddd',
                    textAlign: 'center',
                    backgroundColor: 'white',
                    fontSize: '0.7rem'
                  }}>
                  </td>
                </tr>

                {/* Cheese Category Header */}
                <tr>
                  <td colSpan="6" className="category-header" style={{
                    backgroundColor: '#000',
                    color: 'white',
                    padding: '4px 8px',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                  </td>
                </tr>

                {/* Cheese Items */}
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'cheese', 'American')}>
                    {order.cheese === 'American' ? '✓ American ✓' : 'American'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'cheese', 'Provolone')}>
                    {order.cheese === 'Provolone' ? '✓ Provolone ✓' : 'Provolone'}
                  </td>
                </tr>
                <tr>
                  <td colspan="6" style={getItemStyle(order, 'cheese', 'Pepper Jack')}>
                    {order.cheese === 'Pepper Jack' ? '✓ Pepper Jack ✓' : 'Pepper Jack'}
                  </td>
                </tr>

                {/* Meat Category Header */}
                <tr>
                  <td colSpan="6" className="category-header" style={{
                    backgroundColor: '#000',
                    color: 'white',
                    padding: '4px 8px',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                  </td>
                </tr>

                {/* Meat Items */}
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'meat', 'Ham')}>
                    {order.meat === 'Ham' ? '✓ Ham ✓' : 'Ham'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'meat', 'Turkey')}>
                    {order.meat === 'Turkey' ? '✓ Turkey ✓' : 'Turkey'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'meat', 'Spicy Italian')}>
                    {order.meat === 'Spicy Italian' ? '✓ Spicy Italian ✓' : 'Spicy Italian'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'meat', 'Cold Cut Trio')}>
                    {order.meat === 'Cold Cut Trio' ? '✓ Cold Cut Trio ✓' : 'Cold Cut Trio'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'meat', 'Ham & Turkey')}>
                    {order.meat === 'Ham & Turkey' ? '✓ Ham & Turkey ✓' : 'Ham & Turkey'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'meat', 'Veggie')}>
                    {order.meat === 'Veggie' ? '✓ Veggie ✓' : 'Veggie'}
                  </td>
                </tr>

                {/* Condiments Category Header */}
                <tr>
                  <td colSpan="6" className="category-header" style={{
                    backgroundColor: '#000',
                    color: 'white',
                    padding: '4px 8px',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                  </td>
                </tr>

                {/* Condiments Items */}
                <tr>
                  <td colspan="2" style={getItemStyle(order, 'condiments', 'Mayo')}>
                    {order.condiments && order.condiments.includes('Mayo') ? '✓ Mayo ✓' : 'Mayo'}
                  </td>
                  <td colspan="2" style={getItemStyle(order, 'condiments', 'Mustard')}>
                    {order.condiments && order.condiments.includes('Mustard') ? '✓ Mustard ✓' : 'Mustard'}
                  </td>
                  <td colspan="2" style={getItemStyle(order, 'condiments', 'Ranch')}>
                    {order.condiments && order.condiments.includes('Ranch') ? '✓ Ranch ✓' : 'Ranch'}
                  </td>
                </tr>

                {/* Toppings Category Header */}
                <tr>
                  <td colSpan="6" className="category-header" style={{
                    backgroundColor: '#000',
                    color: 'white',
                    padding: '4px 8px',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                  </td>
                </tr>

                {/* Toppings Items */}
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'toppings', 'Onions')}>
                    {order.toppings && order.toppings.includes('Onions') ? '✓ Onions ✓' : 'Onions'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'toppings', 'Lettuce')}>
                    {order.toppings && order.toppings.includes('Lettuce') ? '✓ Lettuce ✓' : 'Lettuce'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'toppings', 'Tomato')}>
                    {order.toppings && order.toppings.includes('Tomato') ? '✓ Tomato ✓' : 'Tomato'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'toppings', 'Pickles')}>
                    {order.toppings && order.toppings.includes('Pickles') ? '✓ Pickles ✓' : 'Pickles'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'toppings', 'Green Peppers')}>
                    {order.toppings && order.toppings.includes('Green Peppers') ? '✓ Green Peppers ✓' : 'Green Peppers'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'toppings', 'Olives')}>
                    {order.toppings && order.toppings.includes('Olives') ? '✓ Olives ✓' : 'Olives'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'toppings', 'Cucumber')}>
                    {order.toppings && order.toppings.includes('Cucumber') ? '✓ Cucumber ✓' : 'Cucumber'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'toppings', 'Mild Peppers')}>
                    {order.toppings && order.toppings.includes('Mild Peppers') ? '✓ Mild Peppers ✓' : 'Mild Peppers'}
                  </td>
                </tr>

                {/* Cookie Category Header */}
                <tr>
                  <td colSpan="6" className="category-header" style={{
                    backgroundColor: '#000',
                    color: 'white',
                    padding: '4px 8px',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                  </td>
                </tr>

                {/* Cookie Items */}
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'cookie', 'Chocolate Chip')}>
                    {order.cookie === 'Chocolate Chip' ? '✓ Choc Chip ✓' : 'Choc Chip'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'cookie', 'Oatmeal')}>
                    {order.cookie === 'Oatmeal' ? '✓ Oatmeal ✓' : 'Oatmeal'}
                  </td>
                </tr>
                <tr>
                  <td colspan="6" style={getItemStyle(order, 'cookie', 'Raspberry Cheesecake')}>
                    {order.cookie === 'Raspberry Cheesecake' ? '✓ Raspberry Cheesecake ✓' : 'Raspberry Cheesecake'}
                  </td>
                </tr>

                {/* Side Category Header */}
                <tr>
                  <td colSpan="6" className="category-header" style={{
                    backgroundColor: '#000',
                    color: 'white',
                    padding: '4px 8px',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                  </td>
                </tr>

                {/* Side Items */}
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'side', 'Applesauce')}>
                    {order.side === 'Applesauce' ? '✓ Applesauce ✓' : 'Applesauce'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'side', 'Yogurt')}>
                    {order.side === 'Yogurt' ? '✓ Yogurt ✓' : 'Yogurt'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'side', 'Regular Lays')}>
                    {order.side === 'Regular Lays' ? '✓ Regular Lays ✓' : 'Regular Lays'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'side', 'BBQ')}>
                    {order.side === 'BBQ' ? '✓ BBQ ✓' : 'BBQ'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'side', 'Cheddar Sun Chips')}>
                    {order.side === 'Cheddar Sun Chips' ? '✓ Cheddar Sun Chips ✓' : 'Cheddar Sun Chips'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'side', 'Doritos')}>
                    {order.side === 'Doritos' ? '✓ Doritos ✓' : 'Doritos'}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style={getItemStyle(order, 'side', 'Baked Lays')}>
                    {order.side === 'Baked Lays' ? '✓ Baked Lays ✓' : 'Baked Lays'}
                  </td>
                  <td colspan="3" style={getItemStyle(order, 'side', 'Salt & Vinegar')}>
                    {order.side === 'Salt & Vinegar' ? '✓ Salt & Vinegar ✓' : 'Salt & Vinegar'}
                  </td>
                </tr>
              </table>

                    {/* Order Info Footer */}
                    <div style={{
                      fontSize: '0.65rem',
                      color: '#666',
                      marginTop: '8px',
                      borderTop: '1px solid #eee',
                      paddingTop: '6px',
                      textAlign: 'center'
                    }}>
                    </div>
                  </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            ));
          })()
        )}

        {/* Footer */}
        {ordersToPrint.length > 0 && (
          <div className="no-print" style={{
            marginTop: '40px',
            textAlign: 'center',
            color: '#999',
            fontSize: '1rem',
            borderTop: '2px solid #ddd',
            paddingTop: '20px'
          }}>
            <p style={{margin: 0}}>
              📋 End of Orders - Total: {ordersToPrint.length} sandwiches to prepare
            </p>
            <p style={{margin: '10px 0 0 0', fontSize: '0.9rem'}}>
              Generated on {currentDate} at {currentTime}
            </p>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          /* Hide page elements */
          .no-print {
            display: none !important;
          }
          .header {
            display: none !important;
          }
          nav {
            display: none !important;
          }

          /* Remove browser headers/footers (limited browser support) */
          @page {
            margin: 0 !important;
            size: auto;
            /* Attempt to remove headers/footers - browser dependent */
            -webkit-print-headers-footers: none;
            print-headers-footers: none;
          }

          /* Clean page styling */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: none !important;
            -webkit-print-color-adjust: exact !important;
          }

          .print-content {
            margin: 0 !important;
            padding: 10px !important;
            background: none !important;
            min-height: auto !important;
          }

          .category-header {
            background-color: #000 !important;
            color: white !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Remove any default page margins */
          body {
            margin: 0 !important;
          }

          /* Page break styles */
          @media print {
            .page-break {
              page-break-before: always !important;
              break-before: page !important;
            }

            .page-break-avoid {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          }
        }
      `}</style>
    </div>
  );
}

export default Print;
